import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SEO = ({ title, description, name, type, canonicalUrl = 'https://tensorme.com' }) => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  
  const siteTitle = title || "TensorME - Engineering Excellence in Dubai";
  const defaultDescription = description || "Leading provider of specialized engineering services including mechanical, electrical, and control valve services in Dubai, UAE. Expert solutions in pipeline services, equipment procurement, and rental services.";
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "EngineeringOrganization",
    "name": "TensorME Engineering Services",
    "description": defaultDescription,
    "url": canonicalUrl,
    "logo": `${canonicalUrl}/logo.png`,
    "sameAs": [
      "https://www.linkedin.com/company/tensorme",
      "https://twitter.com/tensorme"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Dubai",
      "addressRegion": "Dubai",
      "addressCountry": "UAE"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+971-XXX-XXXX",
      "contactType": "customer service",
      "availableLanguage": ["English", "Arabic"],
      "areaServed": ["AE", "SA", "QA", "KW", "OM", "BH"]
    },
    "offers": {
      "@type": "Offer",
      "serviceType": [
        "Mechanical Engineering Services",
        "Pipeline Services",
        "Electrical & Instrumentation Services",
        "Control Valve Services",
        "Equipment Procurement",
        "Rental Services"
      ]
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "provider": {
      "@type": "Organization",
      "name": "TensorME Engineering Services"
    },
    "serviceType": "Engineering Services",
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "25.2048",
        "longitude": "55.2708"
      },
      "geoRadius": "500"
    }
  };

  return (
    <Helmet>
      {/* Standard metadata tags */}
      <html lang={i18n.language} dir={isArabic ? 'rtl' : 'ltr'} />
      <title>{siteTitle}</title>
      <meta name='description' content={defaultDescription} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Favicon and App Icons */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo192.png" />
      
      {/* OpenGraph/Facebook tags */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="TensorME" />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={defaultDescription} />
      <meta property="og:image" content={`${canonicalUrl}/og-image.jpg`} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={isArabic ? "ar_AE" : "en_US"} />
      <meta property="og:locale:alternate" content={isArabic ? "en_US" : "ar_AE"} />
      
      {/* Twitter tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={name} />
      <meta name="twitter:site" content="@tensorme" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={defaultDescription} />
      <meta name="twitter:image" content={`${canonicalUrl}/twitter-image.jpg`} />
      
      {/* Additional SEO meta tags */}
      <meta name="keywords" content="engineering services dubai, mechanical engineering uae, pipeline services, control valve maintenance, electrical engineering dubai, industrial equipment rental uae" />
      <meta name="author" content="TensorME Engineering Services" />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="theme-color" content="#235688" />
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
    </Helmet>
  );
};

export default SEO; 